import {
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ServiceOffering, ServiceOfferingControllerService } from '../../../core/generated';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AvailabilityService } from '../../../shared/services/availability.service';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ProfileProviderPF } from '../../../core/generated';
import { ProfileProviderPFStateService } from '../../../shared/services/profile-provider-pf-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, distinctUntilChanged, take } from 'rxjs/operators';
import { ContainerComponent } from '../../../shared/container/container.component';

@Component({
  selector: 'app-service-offering-provider-pf',
  standalone: true,
  imports: [CommonModule, MaterialModule, RouterLink, EnumDescriptionPipe, AvailabilityComponent, ContainerComponent],
  templateUrl: './service-offering-provider-pf.component.html',
  styleUrl: './service-offering-provider-pf.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceOfferingProviderPFComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly serviceOfferingService = inject(ServiceOfferingControllerService);
  private readonly profilePFState = inject(ProfileProviderPFStateService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly availabilityService = inject(AvailabilityService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  serviceOfferingList: ServiceOffering[] = [];
  profileProviderPF: ProfileProviderPF | undefined;
  isLoading = false;

  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);

  private currentCpf: string | undefined;

  ngOnInit(): void {
    this.setupContextSubscription();
    this.initialize();
  }

  private setupContextSubscription(): void {
    this.profilePFState.profileProviderPF$
      .pipe(
        filter((profilePF): profilePF is ProfileProviderPF => profilePF !== null),
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((profilePF) => {
        this.handleProfilePFChange(profilePF);
      });
  }

  private handleProfilePFChange(profilePF: ProfileProviderPF): void {
    const hasChanged = !this.profileProviderPF || this.profileProviderPF.id !== profilePF.id;

    if (hasChanged) {
      this.profileProviderPF = profilePF;
      this.serviceOfferingList = [];

      if (profilePF.cpf) {
        this.loadServiceOffering();
      }

      this.cdr.markForCheck();
    }
  }

  async initialize(): Promise<void> {
    // Aguardar o profile do observable com timeout
    const profilePF = await new Promise<ProfileProviderPF | null>((resolve) => {
      let resolved = false;

      const subscription = this.profilePFState.profileProviderPF$
        .pipe(
          filter((profile): profile is ProfileProviderPF => profile !== null),
          take(1)
        )
        .subscribe({
          next: (profile) => {
            if (!resolved) {
              resolved = true;
              resolve(profile);
            }
          },
          error: () => {
            if (!resolved) {
              resolved = true;
              resolve(null);
            }
          }
        });

      // Timeout fallback
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          subscription.unsubscribe();
          resolve(null);
        }
      }, 1500);
    });

    if (profilePF) {
      this.profileProviderPF = profilePF;

      if (profilePF.cpf && this.serviceOfferingList.length === 0) {
        this.loadServiceOffering();
      }
    } else {
      this.redirectToProvider();
    }
  }

  private redirectToProvider(): void {
    const providerPFId = this.route.parent?.snapshot.paramMap.get('id');
    if (providerPFId) {
      this.router.navigate([`/providerPF/${providerPFId}`]);
    }
  }

  loadServiceOffering(): void {
    if (!this.profileProviderPF?.cpf) {
      return;
    }

    if (this.isLoading || this.currentCpf === this.profileProviderPF.cpf) {
      return;
    }

    this.isLoading = true;
    this.currentCpf = this.profileProviderPF.cpf;

    this.serviceOfferingService
      .findByCnpj(this.profileProviderPF.cpf)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.serviceOfferingList = response || [];

          if (this.serviceOfferingList.length === 0) {
            this.navigateToServiceOfferingForm();
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading service offerings:', error);
          this.serviceOfferingList = [];
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private navigateToServiceOfferingForm(): void {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    if (id) {
      this.router.navigate([`providerPF/${id}/serviceOffering/serviceOfferingForm`]);
    }
  }

  getAvailabilityDescription(availability: number[]): string[] {
    return this.availabilityService.getAvailabilityDescription(availability);
  }

  publish(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceOfferingService
      .publish(Number(id), {})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.currentCpf = undefined;
          this.isLoading = false;
          this.loadServiceOffering();
        },
        error: (error) => {
          console.error('Error publishing service offering:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  deleteServiceOffering(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceOfferingService
      .deleteById1(Number(id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.currentCpf = undefined;
          this.isLoading = false;
          this.loadServiceOffering();
        },
        error: (error) => {
          console.error('Error deleting service offering:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  trackByServiceOffering(index: number, item: ServiceOffering): string | number | undefined {
    return item.id || index;
  }
}
